import { addDays, setHours, setMinutes, setSeconds } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useAtomValue } from "jotai";
import React from "react";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "~/components/ui/icons/close";
import { CheckedIllustration } from "~/components/ui/illustrations/checked";
import { brandState } from "~/stores/brand";
import { formatDate } from "~/utils/date";

type DurationWidgetEndedProps = {
  onClose: (widget_id: string) => void;
  endTime: string;
  widget_id: string;
  additionalDays?: number;
};

export const DurationWidgetEnded: React.FC<DurationWidgetEndedProps> = ({
  onClose,
  endTime,
  widget_id,
  additionalDays,
}: DurationWidgetEndedProps) => {
  const { i18n, t } = useTranslation();
  const brand = useAtomValue(brandState);

  const endDateAlmostMidnight = setSeconds(
    setMinutes(setHours(new Date(endTime), 23), 59),
    59,
  );

  function getContent() {
    if (typeof additionalDays === "number") {
      return (
        <div className="flex flex-col items-center">
          <CheckedIllustration className="size-32" />
          <h2 className="mb-4 mt-2 text-2xl font-bold text-gray-900">
            {t("widgets.ended_title_ok", {
              x: additionalDays,
            })}
          </h2>
          <p
            className="mb-6 text-gray-700"
            dangerouslySetInnerHTML={{
              __html: t("widgets.ended_p_ok", {
                x: formatDate(
                  brand?.timezone
                    ? toZonedTime(
                        addDays(endDateAlmostMidnight, additionalDays),
                        brand.timezone,
                      )
                    : addDays(endDateAlmostMidnight, additionalDays),
                  i18n.language.replace("_", "-"),
                  {
                    dateStyle: "long",
                    timeStyle: "long",
                  },
                ),
              }),
            }}
          />
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center">
          <CheckedIllustration className="size-32" />
          <h2 className="mb-4 mt-2 text-2xl font-bold text-gray-900">
            {t("widgets.ended_title_nok")}
          </h2>
          <p
            className="mb-6 text-gray-700"
            dangerouslySetInnerHTML={{
              __html: t("widgets.ended_p_nok", {
                x: formatDate(
                  brand?.timezone
                    ? toZonedTime(endDateAlmostMidnight, brand.timezone)
                    : endDateAlmostMidnight,
                  i18n.language.replace("_", "-"),
                  {
                    dateStyle: "long",
                    timeStyle: "long",
                  },
                ),
              }),
            }}
          />
        </div>
      );
    }
  }

  return (
    <section className="relative mb-4 rounded-lg border border-gray-300 p-8 pt-10">
      {getContent()}
      <button
        type="button"
        className="absolute right-8 top-8 transition-opacity duration-300 hover:opacity-70"
        onClick={() => onClose(widget_id)}
      >
        <CloseIcon className="size-6" />
      </button>
    </section>
  );
};

DurationWidgetEnded.displayName = "DurationWidgetEnded";
